import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Refresh as RefreshIcon,
  LinkOff as LinkOffIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import api from '../api';
import logger from '../utils/logger';
import SalesforceRecordsTable from './SalesforceRecordsTable';
import { useAuth } from '../context/AuthContext';

const SalesforceIntegration = ({ initialTab = 1 }) => {
  const { user, loading: authLoading } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [addonRequired, setAddonRequired] = useState(false);
  const [addonInfo, setAddonInfo] = useState(null);
  const [activatingAddon, setActivatingAddon] = useState(false);
  const [showAddonDialog, setShowAddonDialog] = useState(false);
  
  // Connection form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [securityToken, setSecurityToken] = useState('');
  
  // Normalize initialTab to ensure it's a number (0 or 1)
  const normalizedInitialTab = typeof initialTab === 'number' ? initialTab : (typeof initialTab === 'string' ? parseInt(initialTab, 10) : 1);
  const safeInitialTab = (normalizedInitialTab === 0 || normalizedInitialTab === 1) ? normalizedInitialTab : 1;
  
  // Tab state
  const [activeTab, setActiveTab] = useState(safeInitialTab);
  
  // Update activeTab when initialTab prop changes
  useEffect(() => {
    const normalized = typeof initialTab === 'number' ? initialTab : (typeof initialTab === 'string' ? parseInt(initialTab, 10) : 1);
    const safe = (normalized === 0 || normalized === 1) ? normalized : 1;
    setActiveTab(safe);
  }, [initialTab]);
  
  // Records state
  const [recordType, setRecordType] = useState('Lead');
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Create Lead dialog state
  const [createLeadDialogOpen, setCreateLeadDialogOpen] = useState(false);
  const [creatingLead, setCreatingLead] = useState(false);
  const [newLeadData, setNewLeadData] = useState({
    FirstName: '',
    LastName: '',
    Company: '',
    Email: '',
    Phone: '',
    Title: '',
    Website: '',
    Industry: '',
    Status: 'Open - Not Contacted',
    Description: ''
  });
  const [duplicateLead, setDuplicateLead] = useState(null);
  
  // Pre-populate username when connection status changes (backup)
  useEffect(() => {
    if (connectionStatus?.connection?.username) {
      // Always sync username from connection data
      logger.log('useEffect: Pre-populating username from connectionStatus:', connectionStatus.connection.username);
      setUsername(connectionStatus.connection.username);
    }
  }, [connectionStatus]);
  
  useEffect(() => {
    // Only check connection status if user is authenticated
    if (user && !authLoading) {
      // Verify session is still valid before making API calls
      verifySessionAndCheckStatus();
    } else if (!authLoading && !user) {
      setError('Please log in to use Salesforce integration');
    }
  }, [user, authLoading]);
  
  const verifySessionAndCheckStatus = async () => {
    // First verify the session is still valid
    try {
      const authCheck = await api.get('/check_login');
      if (!authCheck.data.logged_in) {
        setError('Your session has expired. Please log in again.');
        return;
      }
      // Session is valid, proceed with checking Salesforce status
      await checkConnectionStatus();
    } catch (err) {
      logger.error('Error verifying session:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError('Unable to verify session. Please try logging in again.');
      }
    }
  };
  
  const checkConnectionStatus = async () => {
    if (!user) {
      setError('Please log in to use Salesforce integration');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const result = await api.salesforceStatus();
      if (result.success) {
        // Check if add-on is required
        if (result.addon_required) {
          setAddonRequired(true);
          setAddonInfo({
            code: result.addon_code || 'salesforce',
            message: result.message || 'Salesforce add-on required'
          });
          setConnectionStatus({ connected: false, connection: null });
        } else {
          setAddonRequired(false);
          setAddonInfo(null);
          setConnectionStatus(result);
          // Pre-populate username from connection if available
          if (result.connection?.username) {
            logger.log('Pre-populating username:', result.connection.username);
            setUsername(result.connection.username);
          }
        }
      } else {
        setConnectionStatus(null);
        setAddonRequired(false);
        // Check if it's an authentication error
        if (result.error && result.error.includes('Authentication required')) {
          setError('Your session has expired. Please log in again.');
        }
      }
    } catch (err) {
      logger.error('Error checking Salesforce status:', err);
      setConnectionStatus(null);
      setAddonRequired(false);
      // Check if it's a 401 error
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else if (err.response?.status === 403 && err.response?.data?.addon_required) {
        // Add-on required error
        setAddonRequired(true);
        setAddonInfo({
          code: err.response.data.addon_code || 'salesforce',
          message: err.response.data.error || 'Salesforce add-on required'
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubscribeToAddon = async () => {
    if (!addonInfo) return;
    
    try {
      setActivatingAddon(true);
      setError(null);
      
      // Get available add-ons to find the Salesforce add-on
      const addonsResponse = await api.getAddOns();
      if (addonsResponse.success && addonsResponse.data?.addons) {
        const salesforceAddon = addonsResponse.data.addons.find(a => a.code === 'salesforce');
        if (salesforceAddon) {
          const result = await api.activateAddOn(salesforceAddon.id);
          if (result.success) {
            setSuccess('Salesforce add-on activated successfully!');
            setAddonRequired(false);
            setAddonInfo(null);
            setShowAddonDialog(false);
            // Refresh connection status
            await checkConnectionStatus();
          } else {
            setError(result.message || 'Failed to activate add-on');
          }
        } else {
          setError('Salesforce add-on is not available yet. Please contact support or check back later.');
          logger.error('Salesforce add-on not found in available add-ons:', addonsResponse.data.addons);
        }
      } else {
        const errorMsg = addonsResponse.message || 'Failed to load add-ons. Please ensure you have an active subscription.';
        setError(errorMsg);
        logger.error('Failed to load add-ons:', addonsResponse);
      }
    } catch (err) {
      logger.error('Error subscribing to add-on:', err);
      setError(err.response?.data?.message || 'Failed to subscribe to add-on');
    } finally {
      setActivatingAddon(false);
    }
  };
  
  const handleConnect = async () => {
    if (!user) {
      setError('Please log in to use Salesforce integration');
      return;
    }
    
    // Check if add-on is required
    if (addonRequired) {
      setError('Please subscribe to the Salesforce add-on to connect. Click "Subscribe to Add-On" above.');
      return;
    }
    
    // Verify session is still valid before making API call
    try {
      const authCheck = await api.get('/check_login');
      if (!authCheck.data.logged_in) {
        setError('Your session has expired. Please log in again.');
        return;
      }
    } catch (err) {
      logger.error('Error verifying session:', err);
      setError('Your session has expired. Please log in again.');
      return;
    }
    
    if (!username) {
      setError('Username is required');
      return;
    }
    
    if (!password) {
      setError('Password is required');
      return;
    }
    
    if (!securityToken) {
      setError('Security token is required');
      return;
    }
    
    try {
      setConnecting(true);
      setError(null);
      setSuccess(null);
      
      const result = await api.salesforceConnect(username, password, securityToken);
      
      if (result.success) {
        setSuccess('Successfully connected to Salesforce!');
        // Clear password and token for security, but keep username
        setPassword('');
        setSecurityToken('');
        await checkConnectionStatus();
        setActiveTab(1); // Switch to records tab
      } else {
        setError(result.error || 'Failed to connect to Salesforce');
      }
    } catch (err) {
      logger.error('Error connecting to Salesforce:', err);
      // Check if it's an add-on required error
      if (err.response?.status === 403 && err.response?.data?.addon_required) {
        setAddonRequired(true);
        setAddonInfo({
          code: err.response.data.addon_code || 'salesforce',
          message: err.response.data.error || 'Salesforce add-on required'
        });
        setError('Please subscribe to the Salesforce add-on to connect.');
      } else if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to connect to Salesforce');
      }
    } finally {
      setConnecting(false);
    }
  };
  
  const handleDisconnect = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await api.salesforceDisconnect();
      
      if (result.success) {
        setSuccess('Successfully disconnected from Salesforce');
        setConnectionStatus(null);
        setActiveTab(0); // Switch back to connection tab
      } else {
        setError(result.error || 'Failed to disconnect');
      }
    } catch (err) {
      logger.error('Error disconnecting from Salesforce:', err);
      setError(err.response?.data?.error || err.message || 'Failed to disconnect');
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleOpenCreateLeadDialog = () => {
    setNewLeadData({
      FirstName: '',
      LastName: '',
      Company: '',
      Email: '',
      Phone: '',
      Title: '',
      Website: '',
      Industry: '',
      Status: 'Open - Not Contacted',
      Description: ''
    });
    setDuplicateLead(null);
    setError(null);
    setSuccess(null);
    setCreateLeadDialogOpen(true);
  };
  
  const handleCloseCreateLeadDialog = () => {
    setCreateLeadDialogOpen(false);
    setDuplicateLead(null);
    setError(null);
  };
  
  const handleCreateLead = async () => {
    if (!user) {
      setError('Please log in to create leads');
      return;
    }
    
    // Validate required fields
    if (!newLeadData.Company || !newLeadData.LastName) {
      setError('Company and Last Name are required');
      return;
    }
    
    try {
      setCreatingLead(true);
      setError(null);
      setSuccess(null);
      setDuplicateLead(null);
      
      const result = await api.salesforceCreateLead(newLeadData, true);
      
      if (result.success) {
        setSuccess(`Successfully created Lead! Lead ID: ${result.lead_id}`);
        setCreateLeadDialogOpen(false);
        // Reset form
        setNewLeadData({
          FirstName: '',
          LastName: '',
          Company: '',
          Email: '',
          Phone: '',
          Title: '',
          Website: '',
          Industry: '',
          Status: 'Open - Not Contacted',
          Description: ''
        });
        // Trigger refresh of records table
        setRefreshKey(prev => prev + 1);
      } else if (result.is_duplicate && result.existing_lead) {
        setDuplicateLead(result.existing_lead);
        setError(result.error || 'A Lead with this information already exists');
      } else {
        setError(result.error || 'Failed to create Lead');
      }
    } catch (err) {
      logger.error('Error creating Lead:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to create Lead');
      }
    } finally {
      setCreatingLead(false);
    }
  };
  
  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Show message if user is not authenticated
  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Salesforce Integration
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          Please log in to use Salesforce integration.
        </Alert>
      </Box>
    );
  }
  
  // Show add-on required message
  if (addonRequired && !showAddonDialog) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Salesforce Integration
        </Typography>
        
        <Card sx={{ mt: 3, maxWidth: 600, mx: 'auto' }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <CloudOffIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Salesforce Add-On Required
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                To use Salesforce integration, you need to subscribe to the Salesforce add-on.
              </Typography>
            </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                The Salesforce add-on includes:
              </Typography>
              <List dense>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Connect to Salesforce org" />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Create leads and contacts" />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Sync and manage records" />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Bulk operations and data sync" />
                </ListItem>
              </List>
            </Alert>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => setShowAddonDialog(true)}
              >
                View Details
              </Button>
              <Button
                variant="contained"
                onClick={handleSubscribeToAddon}
                disabled={activatingAddon}
                startIcon={activatingAddon ? <CircularProgress size={16} /> : <AddIcon />}
              >
                {activatingAddon ? 'Activating...' : 'Subscribe to Add-On'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Salesforce Integration
      </Typography>
      
      <Paper sx={{ mt: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Connection" />
          <Tab 
            label="Records" 
            icon={connectionStatus?.connected ? <CloudDoneIcon sx={{ color: '#00A1E0' }} /> : <CloudOffIcon />}
            iconPosition="end"
          />
        </Tabs>
        
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            {/* Connection Details Card - Always show if connection exists */}
            {connectionStatus?.connection && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Last Connection Details</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconButton size="small" onClick={checkConnectionStatus}>
                          <RefreshIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  
                  <Box>
                    <Chip 
                      icon={connectionStatus.connected ? <CloudDoneIcon sx={{ color: '#00A1E0' }} /> : <CloudOffIcon />}
                      label={connectionStatus.connected ? "Currently Connected" : "Connection Failed"}
                      sx={{ 
                        mb: 2,
                        ...(connectionStatus.connected ? {
                          borderColor: '#00A1E0',
                          color: '#00A1E0',
                          '& .MuiChip-label': {
                            color: '#00A1E0'
                          }
                        } : {})
                      }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Username:</strong> {connectionStatus.connection.username}
                      </Typography>
                      {connectionStatus.connection.instance_url && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Instance URL:</strong> {connectionStatus.connection.instance_url}
                        </Typography>
                      )}
                      {connectionStatus.connection.created_at && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>First Connected:</strong> {new Date(connectionStatus.connection.created_at).toLocaleString()}
                        </Typography>
                      )}
                      {connectionStatus.connection.last_synced_at && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Last Synced:</strong> {new Date(connectionStatus.connection.last_synced_at).toLocaleString()}
                        </Typography>
                      )}
                      {connectionStatus.connection.last_error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          <strong>Last Error:</strong> {connectionStatus.connection.last_error}
                        </Alert>
                      )}
                    </Box>
                    {connectionStatus.connected && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<LinkOffIcon />}
                        onClick={handleDisconnect}
                        sx={{ mt: 2 }}
                        disabled={loading}
                      >
                        Disconnect
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {/* Connection Status Card - Show when no connection exists */}
            {!connectionStatus?.connection && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Connection Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconButton size="small" onClick={checkConnectionStatus}>
                          <RefreshIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <Chip 
                      icon={<CloudOffIcon />}
                      label="Not Connected"
                      color="default"
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Connect your Salesforce account to view and enrich records
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {/* Connection Form - Always show */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {connectionStatus?.connection ? 'Update Salesforce Connection' : 'Connect to Salesforce'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {connectionStatus?.connection 
                    ? 'Update your Salesforce credentials to reconnect. Your credentials are encrypted and stored securely.'
                    : 'Enter your Salesforce credentials to connect. Your credentials are encrypted and stored securely.'}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500 }}>
                  <TextField
                    label="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    fullWidth
                    required
                    disabled={connecting}
                    helperText={connectionStatus?.connection ? "Pre-filled from last connection" : ""}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    required
                    disabled={connecting}
                    helperText={connectionStatus?.connection ? "Enter new password to update" : ""}
                  />
                  <TextField
                    label="Security Token"
                    type="password"
                    value={securityToken}
                    onChange={(e) => setSecurityToken(e.target.value)}
                    fullWidth
                    required
                    helperText={connectionStatus?.connection ? "Enter new security token to update" : "Required for authentication"}
                    disabled={connecting}
                  />
                  <Button
                    variant="contained"
                    onClick={handleConnect}
                    disabled={connecting || !username || !password || !securityToken}
                    sx={{ mt: 2 }}
                  >
                    {connecting ? <CircularProgress size={24} /> : (connectionStatus?.connection ? 'Update Connection' : 'Connect')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}
          </Box>
        )}
        
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            {loading && !connectionStatus ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: 2 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Checking Salesforce connection...
                </Typography>
              </Box>
            ) : !connectionStatus?.connected ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: 2 }}>
                <CloudOffIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                <Typography variant="h6" color="text.secondary">
                  Not Connected to Salesforce
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
                  Please connect to Salesforce in the Connection tab to view and manage records.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setActiveTab(0)}
                  sx={{ mt: 2 }}
                >
                  Go to Connection Tab
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Salesforce Records</Typography>
                  {recordType === 'Lead' && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenCreateLeadDialog}
                      color="primary"
                    >
                      Add New Lead
                    </Button>
                  )}
                </Box>
                <SalesforceRecordsTable 
                  key={refreshKey}
                  recordType={recordType}
                  onRecordTypeChange={setRecordType}
                />
              </>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Create Lead Dialog */}
      <Dialog
        open={createLeadDialogOpen}
        onClose={handleCloseCreateLeadDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Lead</DialogTitle>
        <DialogContent>
          {duplicateLead && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                A Lead with this information already exists:
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {duplicateLead.FirstName || ''} {duplicateLead.LastName || ''}
                <br />
                <strong>Company:</strong> {duplicateLead.Company || 'N/A'}
                <br />
                <strong>Email:</strong> {duplicateLead.Email || 'N/A'}
                <br />
                <strong>Lead ID:</strong> {duplicateLead.Id}
              </Typography>
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={newLeadData.FirstName}
                onChange={(e) => setNewLeadData({ ...newLeadData, FirstName: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name *"
                value={newLeadData.LastName}
                onChange={(e) => setNewLeadData({ ...newLeadData, LastName: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Company *"
                value={newLeadData.Company}
                onChange={(e) => setNewLeadData({ ...newLeadData, Company: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                value={newLeadData.Email}
                onChange={(e) => setNewLeadData({ ...newLeadData, Email: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={newLeadData.Phone}
                onChange={(e) => setNewLeadData({ ...newLeadData, Phone: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Title"
                value={newLeadData.Title}
                onChange={(e) => setNewLeadData({ ...newLeadData, Title: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newLeadData.Status}
                  onChange={(e) => setNewLeadData({ ...newLeadData, Status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="Open - Not Contacted">Open - Not Contacted</MenuItem>
                  <MenuItem value="Working - Contacted">Working - Contacted</MenuItem>
                  <MenuItem value="Closed - Converted">Closed - Converted</MenuItem>
                  <MenuItem value="Closed - Not Converted">Closed - Not Converted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Website"
                value={newLeadData.Website}
                onChange={(e) => setNewLeadData({ ...newLeadData, Website: e.target.value })}
                fullWidth
                placeholder="https://example.com"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Industry"
                value={newLeadData.Industry}
                onChange={(e) => setNewLeadData({ ...newLeadData, Industry: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={newLeadData.Description}
                onChange={(e) => setNewLeadData({ ...newLeadData, Description: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateLeadDialog} disabled={creatingLead}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateLead}
            variant="contained"
            disabled={creatingLead || !newLeadData.Company || !newLeadData.LastName}
            startIcon={creatingLead ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {creatingLead ? 'Creating...' : 'Create Lead'}
          </Button>
        </DialogActions>
        </Dialog>
      
      {/* Add-On Details Dialog */}
      <Dialog
        open={showAddonDialog}
        onClose={() => setShowAddonDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Subscribe to Salesforce Add-On
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            The Salesforce add-on enables full integration with your Salesforce org, including:
          </Typography>
          <List dense sx={{ mt: 1 }}>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Connect to Salesforce org" />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Create leads and contacts" />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Update existing records" />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Check for duplicate records" />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Bulk record operations" />
            </ListItem>
            <ListItem disablePadding>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="View Salesforce limits" />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              The add-on is billed monthly and can be cancelled anytime. You'll continue to have access until the end of your billing period.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddonDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubscribeToAddon}
            disabled={activatingAddon}
            startIcon={activatingAddon ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {activatingAddon ? 'Activating...' : 'Subscribe Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesforceIntegration;

